import type { FeaturedActionType } from './template';

export const PASS_TYPE_LABELS: Record<string, string> = {
  storeCard: 'Loyalty',
  loyalty: 'Loyalty',
  eventTicket: 'Event ticket',
  generic: 'Generic',
  transit: 'Transit',
  stampCard: 'Stamp card',
};

export const DEVICE_LABELS: Record<string, string> = {
  iphone: 'Apple',
  android: 'Android',
};

export const BARCODE_TYPE_LABELS: Record<string, string> = {
  qr: 'QR',
  code128: 'CODE128',
  aztec: 'AZTEC',
  pdf417: 'PDF417',
};

export const NFC_SOURCE_LABELS: Record<string, string> = {
  uuid: 'UUID',
  extId: 'Ext ID',
  custom: 'Custom',
};

export type PassFieldValues = Record<string, string>;

export interface LocalisedPassContentField {
  value: string;
  localisation: Record<string, string>;
}

export type PassContentValue = string | LocalisedPassContentField;
export type PassContent = Record<string, PassContentValue>;

export interface PassDesignAttributes {
  backgroundColor: string;
  bannerImageUrl: string;
  logoImageUrl: string;
  thumbnailImageUrl: string;
}

export interface PassBarcode {
  enabled: boolean;
  type: 'qr' | 'code128' | 'aztec' | 'pdf417' | null;
  source: 'uuid' | 'extId' | 'custom' | null;
  value: string;
  displayText: boolean;
}

export interface PassNfc {
  enabled: boolean;
  source: 'uuid' | 'extId' | 'custom';
  customValue: string | null;
}

export type PassStatus =
  | 'created'
  | 'installed'
  | 'deactivated'
  | 'expired'
  | 'removed'
  | null;

export type PassDeviceType = 'iphone' | 'android';

// --- API response types ---

export interface PassAttributes {
  extId: string;
  createdAt: string;
  addedAt: string;
  status: PassStatus;
  downloadUrl: string;
  deviceType: PassDeviceType;
  deactivatedAt: string | null;
  passType: string;
  passContent: PassContent;
  passDesign: PassDesignAttributes;
  barcode: PassBarcode;
  nfc: string | PassNfc | null;
  groupTag: string | null;
  groupTags: string[];
  expiresAt: string | null;
  removedAt?: string | null;
  lastScannedAt?: string | null;
  enhancedData?: {
    generic?: {
      featuredActions?: PassStoredFeaturedActions;
    };
  };
}

export interface PassListItemAttributes {
  extId: string;
  passTemplateName: string;
  createdAt: string;
  deviceType: PassDeviceType | null;
  status: PassStatus;
  addedAt: string | null;
  passType: string;
  passContent: PassContent;
  downloadUrl?: string;
  expiresAt?: string | null;
  formSlug?: string;
  formQueryParams?: Array<{ id?: string }>;
}

export interface PassItem {
  id: string;
  type?: string;
  attributes: PassAttributes;
}

export interface PassListItem {
  id: string;
  type?: string;
  attributes: PassListItemAttributes;
}

export interface TemplateField {
  id: string;
  label: string;
  default_value: string;
}

export interface PassTemplateMeta {
  name: string;
  uuid: string;
  template_type: string;
  entity_id: string;
  central_title: string;
  description: string;
  header: {
    header_one: TemplateField;
    header_two: TemplateField;
    header_three: TemplateField;
  };
  primary: {
    primary_one: TemplateField;
    primary_two: TemplateField;
  };
  secondary: {
    sec_one: TemplateField;
    sec_two: TemplateField;
    sec_three: TemplateField;
  };
  auxiliary: {
    aux_one: TemplateField;
    aux_two: TemplateField;
    aux_three: TemplateField;
  };
  back_fields: {
    back_one: TemplateField;
    back_two: TemplateField;
    back_three: TemplateField;
    back_four: TemplateField;
    back_five: TemplateField;
  };
  logo_image: string | null;
  icon_image: string | null;
  banner_image: string | null;
  banner_image_watermarked?: string | null;
  thumbnail_image: string | null;
  monochrome_badge?: string | null;
  apple_banner_image?: string | null;
  apple_banner_image_watermarked?: string | null;
  apple_logo_image?: string | null;
  apple_icon_image?: string | null;
  apple_thumbnail_image?: string | null;
  apple_artwork_image?: string | null;
  apple_secondary_logo_image?: string | null;
  apple_venue_map_image?: string | null;
  google_banner_image: string | null;
  google_banner_image_watermarked?: string | null;
  google_logo_image?: string | null;
  google_wide_logo_image?: string | null;
  chosen_google_logo?: 'icon' | 'wide_logo';
  stamp_zero_image?: string | null;
  text_color: string;
  transit_type: string;
  background_color: string;
  label_color: string;
  apple_app_link: string[];
  google_app_link: {
    play_store_url: string;
    package_name: string;
    web_app_url: string;
    display_text: string;
  };
  watermark?: {
    color?: string;
    position?: string;
  };
  apple_banner_watermark?: {
    color?: string;
    position?: string;
  };
  google_banner_watermark?: {
    color?: string;
    position?: string;
  };
}

export interface GetPassResponse {
  data: {
    data: PassItem;
  };
  meta: {
    template: PassTemplateMeta;
  };
}

export interface ListPassesResponse {
  data: PassListItem[];
  meta?: {
    totalCount?: number;
  };
}

export interface CreatePassResponse {
  data: {
    id: string;
    attributes: {
      downloadUrl: string;
      status: PassStatus;
    };
  };
}

// --- Request types ---

export type PassFeaturedActionPayload = {
  type: FeaturedActionType | null;
  url: string;
} | null;

export type PassStoredFeaturedActions = Partial<
  PassEnhancedDataPayload['generic']['featuredActions']
>;

export interface PassEnhancedDataPayload {
  generic: {
    featuredActions: {
      one: PassFeaturedActionPayload;
      two: PassFeaturedActionPayload;
    };
  };
}

export interface UpdatePassRequest {
  pass: PassFieldValues & {
    barcode?: PassBarcode;
    nfc?: PassNfc;
    expiresAt?: string | null;
    enhancedData?: PassEnhancedDataPayload;
  };
  extId?: string;
  passAction?: 'deactivate' | 'update';
  groupTags?: string[];
}

export interface CreatePassRequest {
  pass: PassFieldValues & {
    barcode?: PassBarcode;
    nfc?: PassNfc;
    expiresAt?: string | null;
    enhancedData?: PassEnhancedDataPayload;
  };
  groupTags?: string[];
}

export interface GroupTagOption {
  label: string;
}

export interface LastScan {
  reader?: Record<string, unknown>;
  createdAt?: string;
}

export interface PassState {
  uuid: string;
  externalId: string;
  url: string;
  status: PassStatus;
  deactivated: boolean | null;
  deviceType: string;
  createdAt: string;
  deactivatedAt: string;
  addedToWalletAt: string;
  lastScan: LastScan;
  barcode: PassBarcode;
  nfc: PassNfc;
  passDesign: PassDesignAttributes;
  passContent: Record<string, string>;
  hasLocalisation?: boolean;
  groupTag: GroupTagOption | string;
  groupTags: GroupTagOption[];
  expiryDate: Date | string;
  expiryTime: string;
  headerOne: string;
  headerTwo: string;
  headerThree: string;
  primaryOne: string;
  primaryTwo: string;
  secOne: string;
  secTwo: string;
  secThree: string;
  auxOne: string;
  auxTwo: string;
  auxThree: string;
  backOne: string;
  backTwo: string;
  backThree: string;
  backFour: string;
  backFive: string;
}

export interface PassTemplateFormatted {
  name: string;
  uuid: string;
  passType: string;
  templateEntity?: string;
  centralTitle: string;
  status?: PassStatus;
  headerOneId: string;
  headerOneLabel: string;
  headerOneValue: string;
  headerTwoId: string;
  headerTwoLabel: string;
  headerTwoValue: string;
  headerThreeId: string;
  headerThreeLabel: string;
  headerThreeValue: string;
  primaryOneId: string;
  primaryOneLabel: string;
  primaryOneValue: string;
  primaryTwoId: string;
  primaryTwoLabel: string;
  primaryTwoValue: string;
  secOneId: string;
  secOneLabel: string;
  secOneValue: string;
  secTwoId: string;
  secTwoLabel: string;
  secTwoValue: string;
  secThreeId: string;
  secThreeLabel: string;
  secThreeValue: string;
  auxOneId: string;
  auxOneLabel: string;
  auxOneValue: string;
  auxTwoId: string;
  auxTwoLabel: string;
  auxTwoValue: string;
  auxThreeId: string;
  auxThreeLabel: string;
  auxThreeValue: string;
  backOneId: string;
  backOneLabel: string;
  backOneValue: string;
  backTwoId: string;
  backTwoLabel: string;
  backTwoValue: string;
  backThreeId: string;
  backThreeLabel: string;
  backThreeValue: string;
  backFourId: string;
  backFourLabel: string;
  backFourValue: string;
  backFiveId: string;
  backFiveLabel: string;
  backFiveValue: string;
  appleAppLink: string[];
  googleAppLinkPlayStoreUrl: string;
  googleAppLinkPackageName: string;
  googleAppLinkWebAppUrl: string;
  googleAppLinkDisplayText: string;
}

export interface PassListFilterValues {
  id: string;
  extId: string;
  extIdPrefix: string;
  passTemplate: string[];
  groupTag: string[];
  passEntity: string;
  status: string[];
  section: string;
  row: string;
  passType: string[];
  deviceType: string;
  search: string;
  addedToWalletDate: string;
}

export interface PassListSortValues {
  sortBy: string;
  sortOrder: string;
}

export type PassListStatus = 'idle' | 'pending' | 'resolved' | 'rejected';

export interface PassListState {
  items: NormalisedPassListItem[];
  total: number;
  pageCount: number;
  status: PassListStatus;
  loading: boolean;
  error: string | null;
}

export interface NormalisedPassListItem extends PassListItem {
  udi: string;
  passUuid: string;
  passType: string;
  templateName: string;
  issuedAt: string;
  deviceType: string;
  addedToWallet: string;
  hasLocalisation: boolean;
}
